<template>
  <div>
    <!-- Main Form Card -->
    <b-card class="bank-reconciliation-card">
      <!-- Gradient Line Wrapper -->
      <div class="gradient-line-wrapper"></div>
      
      <!-- Table Header -->
      <div class="table-header d-flex flex-wrap justify-content-between align-items-center mb-2">
        <div>
          <h5 class="mb-0">Bank Reconciliation</h5>
          <small class="text-muted">Select an account and date to generate reconciliation statement</small>
        </div>
      </div>

      <!-- Filters Row - Single Line -->
      <div class="d-flex flex-wrap align-items-end filter-row-inline">
        <!-- Bank Account Dropdown -->
        <div class="filter-item filter-item-account">
          <label class="filter-label">Account</label>
          <v-select
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :clearable="false"
            :options="accountOptions"
            :reduce="(opt) => opt.id"
            label="title"
            v-model="selectedAccount"
            placeholder="Select Account"
            :disabled="dataLoading || isViewMode"
            @input="onAccountChange"
            append-to-body
            class="filter-control-simple"
          />
        </div>

        <!-- As Of Date -->
        <div class="filter-item filter-item-date">
          <label class="filter-label">As Of</label>
          <flat-pickr
            v-model="dateFrom"
            class="form-control filter-control"
            :config="dateConfig"
            :placeholder="$t('Select Date')"
            :disabled="isViewMode"
            @on-change="onDateFromChange"
          />
        </div>

        <!-- Generate Button -->
        <div class="filter-item filter-item-btn">
          <b-button
            variant="gradient-primary"
            class="action-btn-filter"
            @click="generateReport"
            :disabled="!canGenerateReport || generating || isViewMode"
          >
            <b-spinner v-if="generating" small class="mr-1" />
            <feather-icon v-else icon="RefreshCwIcon" size="16" class="mr-1" />
            <span>{{ generating ? 'Loading...' : 'Generate' }}</span>
          </b-button>
        </div>
      </div>
    </b-card>

    <!-- Existing Reconciliation Records Card -->
    <b-card v-if="!showTable" class="existing-records-card mt-3">
      <!-- Gradient Line Wrapper -->
      <div class="gradient-line-wrapper gradient-cyan"></div>
      
      <!-- Table Header -->
      <div class="table-header d-flex flex-wrap justify-content-between align-items-center mb-2">
        <div>
          <h5 class="mb-0">Existing Reconciliation Records</h5>
          <small class="text-muted">Click on a record to view details</small>
        </div>
      </div>

      <!-- Pagination Controls - Top -->
      <div class="pagination-controls-top d-flex justify-content-between align-items-center mb-3">
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <!-- Page Size Dropdown -->
          <div class="page-size-wrapper">
            <label class="page-size-label">Show:</label>
            <v-select
              v-model="pageSize"
              :options="pageSizeOptions"
              :reduce="option => option.value"
              label="label"
              :clearable="false"
              :searchable="false"
              append-to-body
              class="page-size-select-compact"
              @input="onPageSizeChange"
            />
          </div>
        </div>
        
        <!-- Count Display -->
        <div class="pagination-count-display">
          <span class="count-text">
            Showing <strong>{{ displayedRange.start }}</strong> to <strong>{{ displayedRange.end }}</strong> of <strong>{{ totalRecords }}</strong>
          </span>
        </div>
        
        <!-- Pagination -->
        <div class="pagination-wrapper-compact">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRecords"
            :per-page="pageSize"
            size="sm"
            align="center"
            class="pagination-compact"
            @change="loadExistingRecords"
          />
        </div>
      </div>

      <!-- Loading Spinner -->
      <div v-if="recordsLoading" class="text-center py-4">
        <b-spinner variant="primary" label="Loading..." />
        <p class="text-muted mt-2 mb-0">Loading records...</p>
      </div>

      <!-- Records Table -->
      <div v-else-if="existingRecords.length > 0" class="table-scroll-container">
        <div class="table-wrapper">
          <b-table
            :items="existingRecords"
            :fields="recordFields"
            :responsive="false"
            hover
            show-empty
            class="records-table"
          >
            <template #table-busy>
              <tbody>
                <tr v-for="n in 5" :key="'skeleton-' + n" class="skeleton-row">
                  <td v-for="(field, index) in recordFields" :key="'skeleton-cell-' + index" class="skeleton-cell">
                    <div class="skeleton-loader"></div>
                  </td>
                </tr>
              </tbody>
            </template>
            
            <template #cell(sno)="data">
              <span class="font-weight-bold">{{ data.index + 1 + (currentPage - 1) * pageSize }}</span>
            </template>
            <template #cell(dateFrom)="data">
              <b-badge variant="light-info">{{ formatDisplayDate(data.value) }}</b-badge>
            </template>
            <template #cell(meta_accountTitle)="data">
              <b-badge variant="light-primary">{{ data.value || '-' }}</b-badge>
            </template>
            <template #cell(status)="data">
              <b-badge :variant="data.value === 'balanced' ? 'light-success' : data.value === 'pending' ? 'light-warning' : 'light-secondary'">
                {{ data.value || 'Pending' }}
              </b-badge>
            </template>
            <template #cell(actions)="{ item }">
              <div class="d-flex align-items-center justify-content-end gap-1">
                <b-button
                  variant="gradient-primary"
                  size="sm"
                  v-b-tooltip.hover.left
                  title="View Details"
                  class="action-btn-table"
                  @click="viewRecordDetails(item)"
                  :disabled="viewLoading === item.id"
                >
                  <b-spinner v-if="viewLoading === item.id" small type="grow" />
                  <feather-icon v-else icon="EyeIcon" size="16" />
                </b-button>
                <b-button
                  variant="outline-primary"
                  size="sm"
                  v-b-tooltip.hover.left
                  title="Print"
                  class="action-btn-table"
                  @click="printReconciliation(item)"
                  :disabled="viewLoading === item.id || printLoading === item.id"
                >
                  <b-spinner v-if="printLoading === item.id" small type="grow" />
                  <feather-icon v-else icon="PrinterIcon" size="16" />
                </b-button>
              </div>
            </template>
          </b-table>
        </div>
      </div>

      <!-- No Records Message -->
      <div v-else class="text-center py-4">
        <feather-icon icon="InboxIcon" size="48" class="text-muted mb-2" />
        <p class="text-muted mb-0">No existing reconciliation records found.</p>
      </div>
    </b-card>

    <!-- Reconciliation Table Card -->
    <b-card v-if="showTable" class="reconciliation-table-card mt-3">
      <!-- Gradient Line Wrapper -->
      <div class="gradient-line-wrapper gradient-green"></div>
      
      <!-- Table Header with Balance and Save Button -->
      <div class="table-header d-flex justify-content-between align-items-center mb-2">
        <div class="d-flex align-items-center">
          <b-button
            variant="flat-secondary"
            class="back-btn mr-2"
            @click="goBackToList"
            v-b-tooltip.hover
            title="Back to List"
          >
            <feather-icon icon="ArrowLeftIcon" size="18" />
          </b-button>
          <div>
            <h5 class="mb-0">Reconciliation Details</h5>
            <small class="text-muted">{{ apiAccountTitle || selectedAccountTitle }}</small>
          </div>
        </div>
        <div class="d-flex align-items-center">
          <!-- <div class="closing-balance-display mr-3">
            <span class="balance-label">Balance:</span>
            <span class="balance-value">{{ formatCurrency(closingBalance) }}</span>
          </div> -->
          <div class="status-dropdown-wrapper mr-2">
            <label class="status-label mb-0 mr-1">Status:</label>
            <b-form-select
              v-model="reconciliationStatus"
              :options="statusSelectOptions"
              class="status-select-native"
              size="sm"
            />
          </div>
          <b-button
            variant="gradient-success"
            class="action-btn-filter"
            @click="saveReconciliation"
            :disabled="saving || tableData.length === 0"
          >
            <b-spinner v-if="saving" small class="mr-1" />
            <feather-icon v-else icon="SaveIcon" size="16" class="mr-1" />
            <span>{{ saving ? 'Saving...' : 'Save' }}</span>
          </b-button>
        </div>
      </div>
    
      <!-- Handsontable -->
      <div class="ht-theme-main ht-table-wrapper">
        <b-spinner
          v-if="tableLoading"
          variant="primary"
          label="Loading..."
          class="my-3"
          style="display: block; margin: auto"
        />
        <div v-else-if="tableData.length === 0" class="text-center py-4 text-muted">
          <feather-icon icon="InboxIcon" size="48" class="mb-2" />
          <p class="mb-0">No transactions found for the selected criteria.</p>
        </div>
        <hot-table
          v-else
          ref="hotTable"
          :data="tableData"
          :rowHeaders="true"
          :colHeaders="colHeaders"
          :columns="columns"
          :settings="hotSettings"
        ></hot-table>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BButton,
  BSpinner,
  BTable,
  BPagination,
  BBadge,
  BFormSelect,
  VBTooltip,
} from "bootstrap-vue";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import { mapActions } from "vuex";

import { HotTable } from "@handsontable/vue";
import { registerAllModules } from "handsontable/registry";
import "handsontable/styles/handsontable.min.css";
import "handsontable/styles/ht-theme-main.min.css";

// register Handsontable's modules
registerAllModules();

export default {
  directives: {
    "b-tooltip": VBTooltip,
  },
  components: {
    BCard,
    BButton,
    BSpinner,
    BTable,
    BPagination,
    BBadge,
    BFormSelect,
    vSelect,
    flatPickr,
    HotTable,
  },
  async created() {
    await this.loadAccounts();
    await this.loadExistingRecords();
  },
  computed: {
    accountOptions() {
      return this.accounts.map(account => ({
        id: account.id,
        title: account.title,
        code: account.code,
        nature: account.nature,
      }));
    },
    accountInvalidFeedback() {
      if (!this.selectedAccount) {
        return "Account is required.";
      }
      return "";
    },
    canGenerateReport() {
      return this.selectedAccount && this.dateFrom && !this.generating && !this.isViewMode;
    },
    selectedAccountTitle() {
      const account = this.accounts.find(acc => acc.id === this.selectedAccount);
      return account ? account.title : "";
    },
    displayedRange() {
      const start = this.totalRecords === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
      const end = Math.min(this.currentPage * this.pageSize, this.totalRecords);
      return { start, end };
    },
  },
  data() {
    return {
      accounts: [],
      selectedAccount: null,
      dateFrom: null,
      dataLoading: false,
      generating: false,
      saving: false,
      showTable: false,
      tableLoading: false,
      tableData: [],
      reconciliationId: 0,
      openingBalance: 0,
      closingBalance: 0,
      apiAccountTitle: "",
      isViewMode: false,
      viewLoading: null,
      printLoading: null,
      // Existing records data
      existingRecords: [],
      recordsLoading: false,
      currentPage: 1,
      pageSize: 10,
      totalRecords: 0,
      pageSizeOptions: [
        { value: 10, label: "10" },
        { value: 25, label: "25" },
        { value: 50, label: "50" },
        { value: 100, label: "100" },
      ],
      recordFields: [
        { key: "sno", label: "#", sortable: false, class: "text-center", thStyle: { width: "60px" } },
        { key: "dateFrom", label: "As Of", sortable: true },
        { key: "meta_accountTitle", label: "Account", sortable: true },
        { key: "status", label: "Status", sortable: false, class: "text-center" },
        { key: "actions", label: "Actions", sortable: false, class: "text-right", thStyle: { width: "100px" } },
      ],
      // Status dropdown options
      reconciliationStatus: "pending",
      statusOptions: ["pending", "balanced"],
      statusSelectOptions: [
        { value: "pending", text: "Pending" },
        { value: "balanced", text: "Balanced" },
      ],
      dateConfig: {
        altFormat: "j M, Y",
        altInput: true,
        dateFormat: "Y-m-d",
      },
      colHeaders: ["Title", "Description", "Cheque No", "Add", "Less", "Reconcile", "Presented On", "Balance"],
      columns: [
        { data: "title", type: "text", readOnly: true, width: 150 },
        { data: "description", type: "text", readOnly: true, width: 180 },
        { data: "chequeNo", type: "text", readOnly: true, width: 100 },
        { data: "amountAdd", type: "numeric", numericFormat: { pattern: "0,0.00" }, readOnly: true, width: 100 },
        { data: "amountSubtract", type: "numeric", numericFormat: { pattern: "0,0.00" }, readOnly: true, width: 100 },
        { data: "isPresented", type: "checkbox", width: 70 },
        { 
          data: "dtPresentedOn", 
          type: "date", 
          dateFormat: "DD-MM-YYYY", 
          width: 110,
          renderer: (instance, td, row, col, prop, value, cellProperties) => {
            if (value) {
              const date = new Date(value);
              if (!isNaN(date.getTime())) {
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = date.getFullYear();
                td.textContent = `${day}-${month}-${year}`;
              } else {
                td.textContent = value;
              }
            } else {
              td.textContent = '';
            }
            return td;
          }
        },
        { data: "balance", type: "numeric", numericFormat: { pattern: "0,0.00" }, readOnly: true, width: 120 },
      ],
      hotSettings: {
        licenseKey: "non-commercial-and-evaluation",
        height: "auto",
        stretchH: "all",
        autoWrapRow: true,
        autoWrapCol: true,
        minRows: 1,
        minSpareRows: 0,
        contextMenu: false,
        manualColumnResize: true,
        afterChange: (changes, source) => {
          if (source === "edit" && changes) {
            // Check if isPresented column was changed
            changes.forEach(([row, prop, oldVal, newVal]) => {
              if (prop === "isPresented") {
                // If unchecked, clear the cheque date
                if (!newVal && this.tableData[row]) {
                  this.tableData[row].dtPresentedOn = null;
                }
                // Recalculate balances when reconcile status changes
                this.calculateTotalBalance();
              }
            });
          }
        },
        cells: (row, col) => {
          const cellProperties = {};
          
          // Opening balance row (first row) - all cells read-only
          if (this.tableData && this.tableData[row] && this.tableData[row].isClosingBalance) {
            cellProperties.readOnly = true;
            cellProperties.className = 'opening-balance-row';
            return cellProperties;
          }
          
          // Total balance row (last row) - all cells read-only
          if (this.tableData && this.tableData[row] && this.tableData[row].isTotalBalance) {
            cellProperties.readOnly = true;
            cellProperties.className = 'total-balance-row';
            return cellProperties;
          }
          
          // Cheque Date column (index 6) - only editable if isPresented is true
          if (col === 6 && this.tableData && this.tableData[row]) {
            cellProperties.readOnly = !this.tableData[row].isPresented;
            if (!this.tableData[row].isPresented) {
              cellProperties.className = 'disabled-cell';
            }
          }
          
          return cellProperties;
        },
      },
    };
  },
  methods: {
    ...mapActions({ get: "get", post: "post" }),

    async loadExistingRecords() {
      this.recordsLoading = true;
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "BankReconciliation/GetByPagination?db=" +
            this.$store.state.userData.db +
            "&campusID=" +
            this.$store.state.userData.cId +
            "&page=" +
            this.currentPage +
            "&pageSize=" +
            this.pageSize,
          token: this.$store.state.userData.token,
        };

        const response = await this.get(obj);
        console.log("Existing Records Response:", response);

        // Handle the API response
        if (response && response.status === "success" && response.data) {
          this.existingRecords = response.data.data || response.data || [];
          this.totalRecords = response.data.totalRecords || response.data.total || this.existingRecords.length;
        } else if (response && Array.isArray(response.data)) {
          this.existingRecords = response.data;
          this.totalRecords = response.totalRecords || response.data.length;
        } else if (Array.isArray(response)) {
          this.existingRecords = response;
          this.totalRecords = response.length;
        } else {
          this.existingRecords = [];
          this.totalRecords = 0;
        }
      } catch (error) {
        console.error("Error loading existing records:", error);
        this.existingRecords = [];
        this.totalRecords = 0;
      } finally {
        this.recordsLoading = false;
      }
    },

    formatDisplayDate(dateStr) {
      if (!dateStr) return "-";
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },

    onPageSizeChange() {
      this.currentPage = 1;
      this.loadExistingRecords();
    },

    async viewRecordDetails(record) {
      this.viewLoading = record.id;
      
      try {
        // Step 1: Load the record details
        const detailsObj = {
          url:
            this.$store.state.domain +
            "BankReconciliation/LoadDetails?db=" +
            this.$store.state.userData.db +
            "&campusID=" +
            this.$store.state.userData.cId +
            "&id=" +
            record.id,
          token: this.$store.state.userData.token,
        };

        const response = await this.get(detailsObj);
        console.log("Load Details Response:", response);

        // Handle the API response - check multiple structures
        let data = null;
        
        if (response && response.status === "success" && response.data) {
          data = response.data;
        } else if (response && response.bankReconciliation) {
          // Direct data structure
          data = response;
        } else if (response && response.data && response.data.bankReconciliation) {
          data = response.data;
        }
        
        console.log("Parsed Details Data:", data);

        if (data && data.bankReconciliation) {
          
          // Set the bankReconciliation data
          const bankRec = data.bankReconciliation;
          this.reconciliationId = bankRec.id || 0;
          this.selectedAccount = bankRec.chartOfAccountID;
          this.dateFrom = bankRec.dateFrom ? bankRec.dateFrom.split("T")[0] : null;
          this.apiAccountTitle = bankRec.meta_accountTitle || "";
          this.reconciliationStatus = bankRec.status || "pending";
          
          // Step 2: Call Initialize API to get the closing balance
          const initializeObj = {
            url:
              this.$store.state.domain +
              "BankReconciliation/Initialize?db=" +
              this.$store.state.userData.db +
              "&campusID=" +
              this.$store.state.userData.cId +
              "&dateFrom=" +
              this.dateFrom +
              "&dateTo=" +
              this.dateFrom +
              "&accountID=" +
              this.selectedAccount,
            token: this.$store.state.userData.token,
          };

          const initResponse = await this.get(initializeObj);
          console.log("Initialize Response:", initResponse);
          
          // Parse Initialize response to get closing balance
          let initData = null;
          if (initResponse && initResponse.status === "success" && initResponse.data) {
            initData = initResponse.data;
          } else if (initResponse && initResponse.closingBalance !== undefined) {
            initData = initResponse;
          } else if (initResponse && initResponse.data && initResponse.data.closingBalance !== undefined) {
            initData = initResponse.data;
          }
          
          // Set closing balance from Initialize API
          if (initData && initData.closingBalance !== undefined) {
            this.closingBalance = parseFloat(initData.closingBalance) || 0;
            console.log("Setting closingBalance from Initialize:", this.closingBalance);
          } else {
            // Fallback: try to get from details
            if (data.details && data.details.length > 0) {
              const firstDetail = data.details[0];
              this.closingBalance = parseFloat(firstDetail.openingBalance) || 0;
            } else {
              this.closingBalance = 0;
            }
          }
          
          // First row - Opening balance (read-only)
          const openingBalanceRow = {
            id: -1,
            journalEntryDetailID: 0,
            title: "Balance as per cash book",
            description: "",
            chequeNo: "",
            amountAdd: null,
            amountSubtract: null,
            isPresented: false,
            dtPresentedOn: null,
            balance: this.closingBalance,
            isClosingBalance: true,
          };
          
          // Last row - Total balance (read-only)
          const totalBalanceRow = {
            id: -2,
            journalEntryDetailID: 0,
            title: "Balance as per bank statement",
            description: "",
            chequeNo: "",
            amountAdd: null,
            amountSubtract: null,
            isPresented: false,
            dtPresentedOn: null,
            balance: 0,
            isTotalBalance: true,
          };
          
          // Map details to table data
          const detailsArray = data.details || [];
          if (detailsArray.length > 0) {
            const existingData = detailsArray.map(item => ({
              id: item.id || 0,
              bankReconciliationID: item.bankReconciliationID || 0,
              journalEntryDetailID: item.journalEntryDetailID || 0,
              title: item.meta_accountTitle || item.title || "",
              description: item.description || "",
              chequeNo: item.chequeNo || "",
              amountAdd: parseFloat(item.amountAdd) || 0,
              amountSubtract: parseFloat(item.amountSubtract) || 0,
              isPresented: item.isPresented || false,
              dtPresentedOn: item.dtPresentedOn ? item.dtPresentedOn.split("T")[0] : null,
              balance: 0,
              meta_accountTitle: item.meta_accountTitle || "",
              campusID: item.campusID || this.$store.state.userData.cId,
            }));
            
            // Add opening balance row at start, data in middle, total row at end
            this.tableData = [openingBalanceRow, ...existingData, totalBalanceRow];
          } else {
            // No details - show opening and total rows only
            this.tableData = [openingBalanceRow, totalBalanceRow];
          }
          
          // Calculate total balance
          this.calculateTotalBalance();
          
          // Enable view mode and show table
          this.isViewMode = true;
          this.showTable = true;
          
          // Scroll to the details section
          this.$nextTick(() => {
            const tableCard = document.querySelector(".reconciliation-table-card");
            if (tableCard) {
              tableCard.scrollIntoView({ behavior: "smooth", block: "start" });
            }
          });
        } else {
          console.log("No valid data structure found in response:", response);
          this.$bvToast.toast("Failed to load record details. Invalid response structure.", {
            title: this.$t("Error"),
            variant: "danger",
          });
        }
      } catch (error) {
        console.error("Error loading record details:", error);
        this.$bvToast.toast("Failed to load record details.", {
          title: this.$t("Error"),
          variant: "danger",
        });
      } finally {
        this.viewLoading = null;
      }
    },

    async printReconciliation(record) {
      this.printLoading = record.id;
      
      try {
        // Step 1: Load the record details
        const detailsObj = {
          url:
            this.$store.state.domain +
            "BankReconciliation/LoadDetails?db=" +
            this.$store.state.userData.db +
            "&campusID=" +
            this.$store.state.userData.cId +
            "&id=" +
            record.id,
          token: this.$store.state.userData.token,
        };

        const response = await this.get(detailsObj);
        console.log("Print - Load Details Response:", response);

        // Handle the API response - check multiple structures
        let data = null;
        
        if (response && response.status === "success" && response.data) {
          data = response.data;
        } else if (response && response.bankReconciliation) {
          data = response;
        } else if (response && response.data && response.data.bankReconciliation) {
          data = response.data;
        }
        
        if (data && data.bankReconciliation) {
          const bankRec = data.bankReconciliation;
          const accountTitle = bankRec.meta_accountTitle || "";
          const dateFrom = bankRec.dateFrom ? bankRec.dateFrom.split("T")[0] : "";
          const status = bankRec.status || "pending";
          
          // Step 2: Call Initialize API to get the closing balance
          const initializeObj = {
            url:
              this.$store.state.domain +
              "BankReconciliation/Initialize?db=" +
              this.$store.state.userData.db +
              "&campusID=" +
              this.$store.state.userData.cId +
              "&dateFrom=" +
              dateFrom +
              "&dateTo=" +
              dateFrom +
              "&accountID=" +
              bankRec.chartOfAccountID,
            token: this.$store.state.userData.token,
          };

          const initResponse = await this.get(initializeObj);
          
          // Parse Initialize response to get closing balance
          let initData = null;
          if (initResponse && initResponse.status === "success" && initResponse.data) {
            initData = initResponse.data;
          } else if (initResponse && initResponse.closingBalance !== undefined) {
            initData = initResponse;
          } else if (initResponse && initResponse.data && initResponse.data.closingBalance !== undefined) {
            initData = initResponse.data;
          }
          
          const closingBalance = initData && initData.closingBalance !== undefined 
            ? parseFloat(initData.closingBalance) || 0 
            : 0;
          
          // Get details
          const detailsArray = data.details || [];
          
          // Generate print HTML
          const printContent = this.generatePrintHTML(accountTitle, dateFrom, status, closingBalance, detailsArray);
          
          // Open print window (without auto-printing)
          const printWindow = window.open('', '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes');
          
          if (printWindow) {
            printWindow.document.write(printContent);
            printWindow.document.close();
          } else {
            this.$bvToast.toast("Pop-up blocked! Please allow pop-ups for this site.", {
              title: this.$t("Error"),
              variant: "warning",
            });
          }
        } else {
          this.$bvToast.toast("Failed to load record details for printing.", {
            title: this.$t("Error"),
            variant: "danger",
          });
        }
      } catch (error) {
        console.error("Error printing reconciliation:", error);
        this.$bvToast.toast("Failed to load reconciliation data for printing.", {
          title: this.$t("Error"),
          variant: "danger",
        });
      } finally {
        this.printLoading = null;
      }
    },

    generatePrintHTML(accountTitle, dateFrom, status, closingBalance, detailsArray) {
      // Separate transactions into reconciled and unreconciled
      const reconciledTransactions = [];
      const unreconciledTransactions = [];
      
      let runningBalance = closingBalance;
      
      // Process reconciled transactions (isPresented = true)
      detailsArray.forEach(item => {
        const amountAdd = parseFloat(item.amountAdd) || 0;
        const amountSubtract = parseFloat(item.amountSubtract) || 0;
        
        if (item.isPresented) {
          runningBalance += amountAdd;
          runningBalance -= amountSubtract;
          
          reconciledTransactions.push({
            title: item.meta_accountTitle || item.title || "",
            description: item.description || "",
            chequeNo: item.chequeNo || "",
            amountAdd: amountAdd,
            amountSubtract: amountSubtract,
            dtPresentedOn: item.dtPresentedOn ? this.formatDateDDMMYYYY(item.dtPresentedOn) : "",
            balance: runningBalance,
          });
        } else {
          unreconciledTransactions.push({
            title: item.meta_accountTitle || item.title || "",
            description: item.description || "",
            chequeNo: item.chequeNo || "",
            amountAdd: amountAdd,
            amountSubtract: amountSubtract,
            dtPresentedOn: item.dtPresentedOn ? this.formatDateDDMMYYYY(item.dtPresentedOn) : "",
          });
        }
      });
      
      const reconciledTotal = runningBalance;
      const finalBalance = runningBalance; // Final balance is same as reconciled total since unreconciled don't affect balance
      
      // Calculate projected balance if unreconciled transactions were reconciled
      let projectedBalance = runningBalance;
      unreconciledTransactions.forEach(item => {
        projectedBalance += item.amountAdd;
        projectedBalance -= item.amountSubtract;
      });
      
      const formattedDate = dateFrom ? new Date(dateFrom).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      }) : "";
      
      return `
        <!DOCTYPE html>
        <html lang="en">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Bank Reconciliation - ${accountTitle}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            body {
              font-family: 'Arial', sans-serif;
              line-height: 1.6;
              color: #1f2937;
              background-color: #fff;
              padding: 30px;
              font-size: 12px;
            }
            
            .print-header {
              text-align: center;
              margin-bottom: 30px;
              padding: 20px;
              border-bottom: 3px solid #7c5dfa;
            }
            
            .print-header h1 {
              margin: 0 0 10px 0;
              color: #1f2937;
              font-size: 24px;
              font-weight: 700;
            }
            
            .print-header .subtitle {
              font-size: 14px;
              color: #6b7280;
              margin: 5px 0;
            }
            
            .info-section {
              margin-bottom: 25px;
              padding: 15px;
              background: #f9fafb;
              border-radius: 8px;
              border-left: 4px solid #7c5dfa;
            }
            
            .info-row {
              display: flex;
              justify-content: space-between;
              margin-bottom: 8px;
              font-size: 13px;
            }
            
            .info-label {
              font-weight: 600;
              color: #4a5568;
              min-width: 150px;
            }
            
            .info-value {
              color: #1f2937;
              font-weight: 500;
            }
            
            .balance-section {
              margin-bottom: 25px;
              padding: 20px;
              background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
              border-radius: 8px;
              border: 2px solid #86efac;
            }
            
            .balance-row {
              display: flex;
              justify-content: space-between;
              align-items: center;
            }
            
            .balance-label {
              font-size: 14px;
              font-weight: 600;
              color: #166534;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            
            .balance-value {
              font-size: 18px;
              font-weight: 700;
              color: #15803d;
            }
            
            .section-title {
              font-size: 16px;
              font-weight: 700;
              color: #1f2937;
              margin: 30px 0 15px 0;
              padding-bottom: 10px;
              border-bottom: 2px solid #e5e7eb;
            }
            
            .report-table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 20px;
              background: white;
              border: 1px solid #e5e7eb;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
            }
            
            .report-table th {
              background: linear-gradient(180deg, #f8f7ff 0%, #f0edff 100%);
              color: #4a5568;
              padding: 12px 10px;
              text-align: left;
              font-weight: 700;
              font-size: 11px;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              border: 1px solid #e0d8ff;
            }
            
            .report-table th:nth-child(4),
            .report-table th:nth-child(5),
            .report-table th:nth-child(7) {
              text-align: right;
            }
            
            .report-table th:nth-child(6) {
              text-align: center;
            }
            
            .report-table td {
              padding: 10px;
              border: 1px solid #e5e7eb;
              font-size: 11px;
              vertical-align: middle;
            }
            
            .report-table tbody tr:hover {
              background-color: #f9fafb;
            }
            
            .text-right {
              text-align: right;
            }
            
            .text-center {
              text-align: center;
            }
            
            .report-table td:nth-child(6) {
              text-align: center !important;
              white-space: nowrap;
            }
            
            .total-row {
              background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%) !important;
              font-weight: 700 !important;
            }
            
            .total-row td {
              color: #1e40af !important;
              font-size: 12px !important;
            }
            
            .final-balance-section {
              margin-top: 30px;
              padding: 20px;
              background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
              border-radius: 8px;
              border: 2px solid #93c5fd;
            }
            
            .print-controls {
              position: fixed;
              top: 20px;
              right: 20px;
              z-index: 1000;
            }
            
            .print-btn {
              background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
              color: white;
              border: none;
              padding: 12px 24px;
              border-radius: 8px;
              cursor: pointer;
              font-size: 14px;
              font-weight: 600;
              box-shadow: 0 4px 12px rgba(124, 93, 250, 0.3);
              transition: all 0.2s ease;
              display: inline-flex;
              align-items: center;
              gap: 8px;
            }
            
            .print-btn:hover {
              transform: translateY(-2px);
              box-shadow: 0 6px 16px rgba(124, 93, 250, 0.4);
            }
            
            .print-btn:active {
              transform: translateY(0);
            }
            
            .print-btn-icon {
              width: 18px;
              height: 18px;
            }
            
            @media print {
              @page {
                margin: 0.5in;
                size: A4 landscape;
              }
              
              body {
                padding: 15px;
                font-size: 11px;
              }
              
              .print-header {
                page-break-after: avoid;
              }
              
              .balance-section {
                page-break-after: avoid;
              }
              
              .section-title {
                page-break-after: avoid;
              }
              
              .report-table {
                page-break-inside: auto;
              }
              
              .report-table tr {
                page-break-inside: avoid;
                page-break-after: auto;
              }
              
              .final-balance-section {
                page-break-before: avoid;
              }
              
              .print-controls {
                display: none !important;
              }
            }
          </style>
        </head>
        <body>
          <div class="print-controls">
            <button class="print-btn" onclick="window.print()">
              <svg class="print-btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"></path>
              </svg>
              Print Report
            </button>
          </div>
          
          <div class="print-header">
            <h1>Bank Reconciliation Statement</h1>
            <div class="subtitle">${accountTitle}</div>
            <div class="subtitle">As Of: ${formattedDate}</div>
          </div>
          
          <div class="info-section">
            <div class="info-row">
              <span class="info-label">Account:</span>
              <span class="info-value">${accountTitle}</span>
            </div>
            <div class="info-row">
              <span class="info-label">As Of Date:</span>
              <span class="info-value">${formattedDate}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Status:</span>
              <span class="info-value">${status.charAt(0).toUpperCase() + status.slice(1)}</span>
            </div>
          </div>
          
          <div class="balance-section">
            <div class="balance-row">
              <span class="balance-label">Balance as per Cash Book:</span>
              <span class="balance-value">${this.formatCurrency(closingBalance)}</span>
            </div>
          </div>
          
          ${reconciledTransactions.length > 0 ? `
            <div class="section-title">Reconciled Transactions</div>
            <table class="report-table">
              <thead>
                <tr>
                  <th style="width: 18%;">Title</th>
                  <th style="width: 25%;">Description</th>
                  <th style="width: 12%;">Cheque No</th>
                  <th style="width: 12%;">Add</th>
                  <th style="width: 12%;">Less</th>
                  <th style="width: 15%;">Presented On</th>
                  <th style="width: 15%;">Balance</th>
                </tr>
              </thead>
              <tbody>
                ${reconciledTransactions.map(row => `
                  <tr>
                    <td>${row.title}</td>
                    <td>${row.description}</td>
                    <td>${row.chequeNo || '-'}</td>
                    <td class="text-right">${row.amountAdd > 0 ? this.formatCurrency(row.amountAdd) : '-'}</td>
                    <td class="text-right">${row.amountSubtract > 0 ? this.formatCurrency(row.amountSubtract) : '-'}</td>
                    <td class="text-center">${row.dtPresentedOn || '-'}</td>
                    <td class="text-right">${this.formatCurrency(row.balance)}</td>
                  </tr>
                `).join('')}
                <tr class="total-row">
                  <td colspan="6" style="text-align: right; font-weight: 700;">Total Balance after Reconciled Transactions:</td>
                  <td class="text-right">${this.formatCurrency(reconciledTotal)}</td>
                </tr>
              </tbody>
            </table>
          ` : ''}
          
          ${unreconciledTransactions.length > 0 ? `
            <div class="section-title">Unreconciled Transactions</div>
            <table class="report-table">
              <thead>
                <tr>
                  <th style="width: 18%;">Title</th>
                  <th style="width: 25%;">Description</th>
                  <th style="width: 12%;">Cheque No</th>
                  <th style="width: 12%;">Add</th>
                  <th style="width: 12%;">Less</th>
                  <th style="width: 15%;">Presented On</th>
                  <th style="width: 15%;">Balance</th>
                </tr>
              </thead>
              <tbody>
                ${unreconciledTransactions.map(row => `
                  <tr>
                    <td>${row.title}</td>
                    <td>${row.description}</td>
                    <td>${row.chequeNo || '-'}</td>
                    <td class="text-right">${row.amountAdd > 0 ? this.formatCurrency(row.amountAdd) : '-'}</td>
                    <td class="text-right">${row.amountSubtract > 0 ? this.formatCurrency(row.amountSubtract) : '-'}</td>
                    <td class="text-center">${row.dtPresentedOn || '-'}</td>
                    <td class="text-right">-</td>
                  </tr>
                `).join('')}
                <tr class="total-row" style="background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%) !important;">
                  <td colspan="6" style="text-align: right; font-weight: 700; color: #92400e !important;">
                    Projected Balance (if all unreconciled transactions were reconciled):
                  </td>
                  <td class="text-right" style="color: #92400e !important;">${this.formatCurrency(projectedBalance)}</td>
                </tr>
              </tbody>
            </table>
          ` : ''}
          
          <div class="final-balance-section">
            <div class="balance-row">
              <span class="balance-label" style="color: #1e40af;">Balance as per Bank Statement:</span>
              <span class="balance-value" style="color: #1e40af;">${this.formatCurrency(finalBalance)}</span>
            </div>
            ${unreconciledTransactions.length > 0 ? `
              <div class="balance-row" style="margin-top: 12px; padding-top: 12px; border-top: 1px solid #93c5fd;">
                <span class="balance-label" style="color: #92400e; font-size: 13px;">Projected Balance (if unreconciled were reconciled):</span>
                <span class="balance-value" style="color: #92400e; font-size: 16px;">${this.formatCurrency(projectedBalance)}</span>
              </div>
            ` : ''}
          </div>
          
          <div style="margin-top: 30px; padding-top: 20px; border-top: 2px solid #e5e7eb; text-align: center; color: #6b7280; font-size: 10px;">
            <p>Generated on ${new Date().toLocaleDateString('en-GB', { 
              day: '2-digit', 
              month: 'long', 
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}</p>
          </div>
        </body>
        </html>
      `;
    },

    goBackToList() {
      this.showTable = false;
      this.isViewMode = false;
      this.tableData = [];
      this.reconciliationId = 0;
      this.selectedAccount = null;
      this.dateFrom = null;
      this.apiAccountTitle = "";
      this.closingBalance = 0;
      this.reconciliationStatus = "pending";
      // Reload existing records
      this.loadExistingRecords();
    },

    async loadAccounts() {
      this.dataLoading = true;
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "Accounts/LoadData?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
        };
        
        const response = await this.get(obj);
        
        // Handle the API response structure
        if (response && response.status === "success" && response.data) {
          this.accounts = response.data;
        } else if (Array.isArray(response)) {
          // Fallback for old API format
          this.accounts = response;
        } else {
          this.accounts = [];
        }
      } catch (error) {
        console.error("Error loading accounts:", error);
        this.$bvToast.toast("Failed to load accounts.", {
          title: this.$t("Error"),
          variant: "danger",
        });
        this.accounts = [];
      } finally {
        this.dataLoading = false;
      }
    },

    onAccountChange(accountId) {
      this.selectedAccount = accountId;
    },

    onDateFromChange(selectedDates, dateStr) {
      if (dateStr) {
        this.dateFrom = dateStr;
      }
    },

    formatCurrency(value) {
      const num = parseFloat(value) || 0;
      return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    formatDateDDMMYYYY(dateStr) {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    },

    async generateReport() {
      // Validate inputs
      if (!this.selectedAccount) {
        this.$refs.bankAccount.state = false;
        return;
      }
      if (!this.dateFrom) {
        this.$refs.asOfDate.state = false;
        return;
      }

      this.generating = true;
      this.tableLoading = true;
      this.showTable = true;
      
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "BankReconciliation/Initialize?db=" +
            this.$store.state.userData.db +
            "&campusID=" +
            this.$store.state.userData.cId +
            "&dateFrom=" +
            this.dateFrom +
            "&dateTo=" +
            this.dateFrom +
            "&accountID=" +
            this.selectedAccount,
          token: this.$store.state.userData.token,
        };

        const response = await this.get(obj);
        
        console.log("API Response:", response);
        
        // Handle the API response
        let data = null;
        
        if (response && response.status === "success" && response.data) {
          data = response.data;
        } else if (response && response.closingBalance !== undefined) {
          data = response;
        } else if (response && response.data && response.data.closingBalance !== undefined) {
          data = response.data;
        }
        
        console.log("Parsed data:", data);
        
        if (data && data.closingBalance !== undefined) {
          // Extract account title from API
          this.apiAccountTitle = data.accountTitle || this.selectedAccountTitle;
          
          // Set closing balance
          this.closingBalance = parseFloat(data.closingBalance) || 0;
          
          console.log("Setting closingBalance to:", this.closingBalance);
          
          // Check for allDetails
          const detailsArray = data.allDetails || data.details;
          const hasAllDetails = detailsArray && Array.isArray(detailsArray) && detailsArray.length > 0;
          
          // First row - Opening balance (read-only)
          const openingBalanceRow = {
            id: -1,
            journalEntryDetailID: 0,
            title: "Balance as per cash book",
            description: "",
            chequeNo: "",
            amountAdd: null,
            amountSubtract: null,
            isPresented: false,
            dtPresentedOn: null,
            balance: this.closingBalance,
            isClosingBalance: true,
          };
          
          // Last row - Total balance (read-only)
          const totalBalanceRow = {
            id: -2,
            journalEntryDetailID: 0,
            title: "Balance as per bank statement",
            description: "",
            chequeNo: "",
            amountAdd: null,
            amountSubtract: null,
            isPresented: false,
            dtPresentedOn: null,
            balance: 0,
            isTotalBalance: true,
          };
          
          if (hasAllDetails) {
            // Map allDetails to table rows
            const existingData = detailsArray.map(item => ({
              id: item.id || 0,
              journalEntryDetailID: item.journalEntryDetailID || 0,
              title: item.meta_accountTitle || item.title || item.Title || "",
              description: item.description || item.Description || "",
              chequeNo: item.chequeNo || item.ChequeNo || "",
              amountAdd: parseFloat(item.amountAdd || item.AmountAdd) || 0,
              amountSubtract: parseFloat(item.amountSubtract || item.AmountSubtract) || 0,
              isPresented: item.isPresented || false,
              dtPresentedOn: item.dtPresentedOn || null,
              balance: 0,
              meta_accountTitle: item.meta_accountTitle || "",
              campusID: item.campusID || this.$store.state.userData.cId,
            }));
            
            // Add opening balance row at start, data in middle, total row at end
            this.tableData = [openingBalanceRow, ...existingData, totalBalanceRow];
          } else {
            // No details - show opening and total rows only
            this.tableData = [openingBalanceRow, totalBalanceRow];
          }
          
          // Calculate total balance
          this.calculateTotalBalance();
          
          this.$bvToast.toast("Reconciliation data loaded successfully.", {
            title: this.$t("Success"),
            variant: "success",
          });
        } else {
          // No valid data found
          console.log("No valid data found");
          this.closingBalance = 0;
          this.tableData = [];
          this.$bvToast.toast("No transactions found for the selected criteria.", {
            title: "Info",
            variant: "info",
          });
        }
        
      } catch (error) {
        console.error("Error generating report:", error);
        this.closingBalance = 0;
        this.tableData = [];
        this.$bvToast.toast("Failed to load reconciliation data.", {
          title: this.$t("Error"),
          variant: "danger",
        });
      } finally {
        this.generating = false;
        this.tableLoading = false;
      }
    },

    async saveReconciliation() {
      if (this.tableData.length === 0) {
        this.$bvToast.toast("No data to save.", {
          title: "Validation Error",
          variant: "warning",
        });
        return;
      }

      this.saving = true;
      
      try {
        // Filter out special rows (opening balance and total balance)
        const dataRows = this.tableData.filter(row => !row.isClosingBalance && !row.isTotalBalance);
        
        // Prepare details with the new structure
        const details = dataRows.map(row => {
          // Format dtPresentedOn to ISO format (YYYY-MM-DDTHH:mm:ss) for API
          let formattedDate = null;
          if (row.isPresented && row.dtPresentedOn) {
            try {
              // If it's already a Date object or ISO string
              const date = new Date(row.dtPresentedOn);
              if (!isNaN(date.getTime())) {
                // Format as YYYY-MM-DDTHH:mm:ss
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                formattedDate = `${year}-${month}-${day}T00:00:00`;
              } else {
                // Try parsing DD-MM-YYYY format
                const ddmmyyyy = row.dtPresentedOn.match(/(\d{2})-(\d{2})-(\d{4})/);
                if (ddmmyyyy) {
                  formattedDate = `${ddmmyyyy[3]}-${ddmmyyyy[2]}-${ddmmyyyy[1]}T00:00:00`;
                } else {
                  formattedDate = null;
                }
              }
            } catch (error) {
              console.error("Error formatting date:", error, row.dtPresentedOn);
              formattedDate = null;
            }
          }
          
          return {
            id: row.id || 0,
            journalEntryDetailID: row.journalEntryDetailID || 0,
            title: row.title || "",
            description: row.description || "",
            chequeNo: row.chequeNo || "",
            amountAdd: parseFloat(row.amountAdd) || 0,
            amountSubtract: parseFloat(row.amountSubtract) || 0,
            isPresented: row.isPresented || false,
            dtPresentedOn: formattedDate,
            meta_accountTitle: row.meta_accountTitle || this.apiAccountTitle || "",
            campusID: row.campusID || this.$store.state.userData.cId,
          };
        });

        const payload = {
          bankReconciliation: {
            id: this.reconciliationId || 0,
            dateFrom: this.dateFrom + "T00:00:00",
            dateTo: this.dateFrom + "T00:00:00",
            meta_accountTitle: this.apiAccountTitle || this.selectedAccountTitle,
            chartOfAccountID: this.selectedAccount,
            status: this.reconciliationStatus || "pending",
            campusID: this.$store.state.userData.cId,
            closingBalance: this.closingBalance || 0,
          },
          details: details,
        };
        
        console.log("Save Payload:", payload);

        const obj = {
          url:
            this.$store.state.domain +
            "BankReconciliation/Save?db=" +
            this.$store.state.userData.db +
            "&campusID=" +
            this.$store.state.userData.cId +
            "&userID=" +
            this.$store.state.userData.userID,
          body: payload,
          token: this.$store.state.userData.token,
          message: "Reconciliation saved successfully!",
          context: this,
        };

        const success = await this.post(obj);
        
        console.log("Save Response:", success);
        
        if (success) {
          // Go back to list after successful save
          this.goBackToList();
        }
        
      } catch (error) {
        console.error("Error saving reconciliation:", error);
        this.$bvToast.toast("Failed to save reconciliation.", {
          title: this.$t("Error"),
          variant: "danger",
        });
      } finally {
        this.saving = false;
      }
    },

    calculateTotalBalance() {
      // Start with opening balance
      let runningBalance = this.closingBalance;
      
      // Calculate running balance for each row
      this.tableData.forEach((row, index) => {
        if (row.isClosingBalance) {
          // Opening balance row - show the opening balance
          row.balance = this.closingBalance;
        } else if (row.isTotalBalance) {
          // Total row - will be set at the end
          // Skip for now
        } else {
          // Data rows - only add to balance if reconciled (isPresented is true)
          if (row.isPresented) {
            runningBalance += parseFloat(row.amountAdd) || 0;
            runningBalance -= parseFloat(row.amountSubtract) || 0;
          }
          row.balance = runningBalance;
        }
      });
      
      // Update the total balance row with final balance
      const totalRowIndex = this.tableData.findIndex(row => row.isTotalBalance);
      if (totalRowIndex !== -1) {
        this.tableData[totalRowIndex].balance = runningBalance;
      }
      
      // Refresh the table
      this.$nextTick(() => {
        if (this.$refs.hotTable && this.$refs.hotTable.hotInstance) {
          this.$refs.hotTable.hotInstance.render();
        }
      });
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";

/* Global Date Picker Calendar Styling - Compact & Aligned */
.pika-single {
  font-size: 11px !important;
  border-radius: 6px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12) !important;
  border: 1px solid #e5e7eb !important;
  padding: 6px !important;
  background: white !important;
  min-width: 240px !important;
  max-width: 240px !important;
}

.pika-lendar {
  width: 240px !important;
  padding: 0 !important;
  margin: 0 auto !important;
}

.pika-title {
  font-size: 12px !important;
  padding: 4px 0 !important;
  margin-bottom: 2px !important;
  color: #1f2937 !important;
  text-align: center !important;
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
}

.pika-label {
  font-size: 12px !important;
  font-weight: 600 !important;
  color: #1f2937 !important;
}

.pika-prev,
.pika-next {
  width: 20px !important;
  height: 20px !important;
  padding: 0 !important;
  margin: 0 !important;
  border-radius: 4px !important;
  transition: all 0.2s ease !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  cursor: pointer !important;
}

.pika-prev:hover,
.pika-next:hover {
  background: #f3f4f6 !important;
}

.pika-table {
  margin: 0 auto !important;
  width: 100% !important;
  border-collapse: collapse !important;
}

.pika-table th {
  font-size: 9px !important;
  font-weight: 600 !important;
  color: #6b7280 !important;
  padding: 4px 2px !important;
  text-transform: uppercase !important;
  letter-spacing: 0.3px !important;
  border: none !important;
  text-align: center !important;
  width: 14.28% !important;
}

.pika-table td {
  padding: 2px !important;
  border: none !important;
  text-align: center !important;
  width: 14.28% !important;
}

.pika-button {
  width: 24px !important;
  height: 24px !important;
  font-size: 10px !important;
  line-height: 24px !important;
  border-radius: 3px !important;
  margin: 0 auto !important;
  padding: 0 !important;
  text-align: center !important;
  transition: all 0.15s ease !important;
  display: block !important;
  color: #374151 !important;
}

.pika-button:hover {
  background: #f3f4f6 !important;
  color: #1f2937 !important;
}

.is-selected .pika-button {
  background: #7c5dfa !important;
  color: white !important;
  font-weight: 600 !important;
  box-shadow: 0 1px 3px rgba(124, 93, 250, 0.4) !important;
}

.is-today .pika-button {
  font-weight: 600 !important;
  color: #7c5dfa !important;
  border: 1.5px solid #7c5dfa !important;
  background: rgba(124, 93, 250, 0.1) !important;
}

.is-disabled .pika-button {
  color: #d1d5db !important;
  cursor: not-allowed !important;
  opacity: 0.5 !important;
}

.is-outside-month .pika-button {
  color: #d1d5db !important;
  opacity: 0.4 !important;
}
</style>

<style lang="scss" scoped>
/* Card Base Styling */
.bank-reconciliation-card,
.existing-records-card,
.reconciliation-table-card {
  background: #ffffff;
  border: none;
  border-radius: 16px;
  padding: 1.25rem;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
  position: relative;
  overflow: visible;
}

/* Gradient Line Wrapper */
.gradient-line-wrapper {
  position: absolute;
  top: 0;
  left: 0.75rem;
  right: 0.75rem;
  width: calc(100% - 1.5rem);
  height: 4px;
  background: linear-gradient(90deg, #7c5dfa 0%, #9d7aff 50%, #c4b5fd 100%);
  border-radius: 16px 16px 0 0;
  z-index: 1;

  &.gradient-cyan {
    background: linear-gradient(90deg, #00cfe8 0%, #3dd5f3 50%, #7ae3f4 100%);
  }

  &.gradient-green {
    background: linear-gradient(90deg, #28c76f 0%, #48da89 50%, #81e6a8 100%);
  }
}

/* Table Header */
.table-header {
  h5 {
    font-size: 1.1rem;
    font-weight: 700;
    color: #2d3748;
    margin-bottom: 0;
  }

  small {
    font-size: 0.8rem;
    color: #718096;
  }
}

/* Filter Row Inline */
.filter-row-inline {
  gap: 1rem;
  width: 100%;

  .filter-item {
    flex-shrink: 0;
  }

  .filter-item-account {
    flex: 0 0 40%;
    min-width: 200px;
  }

  .filter-item-date {
    flex: 0 0 30%;
    min-width: 150px;
  }

  .filter-item-btn {
    flex: 0 0 auto;
    display: flex;
    align-items: flex-end;
  }

  @media (max-width: 767px) {
    .filter-item {
      flex: 0 0 100% !important;
      margin-bottom: 0.75rem;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}

/* Filter Label */
.filter-label {
  font-weight: 600;
  color: #4a5568;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.35rem;
  display: block;
}

/* Filter Control */
.filter-control {
  height: 42px !important;
  min-height: 42px !important;
  border-radius: 10px;
  border: 1.5px solid #e0d8ff;
  padding: 0.5rem 0.75rem;
  font-size: 0.9rem;
  color: #2d3748;
  background: #fff;
  transition: all 0.2s ease;

  &:hover {
    border-color: #c4b5fd;
  }

  &:focus {
    border-color: #7c5dfa;
    box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1);
    outline: none;
  }

  &::placeholder {
    color: #a0aec0;
  }

  &:disabled {
    background: #f7fafc;
    cursor: not-allowed;
    opacity: 0.7;
  }
}

/* Vue Select Filter Control - Simple */
::v-deep .filter-control-simple {
  width: 100%;

  .vs__dropdown-toggle {
    height: 42px !important;
    min-height: 42px !important;
    border-radius: 10px;
    border: 1.5px solid #e0d8ff;
    padding: 0.25rem 0.75rem;
    background: #fff;
    transition: all 0.2s ease;
  }

  &:hover .vs__dropdown-toggle {
    border-color: #c4b5fd;
  }

  .vs__dropdown-toggle:focus-within {
    border-color: #7c5dfa;
    box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1);
  }

  .vs__selected {
    color: #2d3748 !important;
    font-weight: 500;
    font-size: 0.9rem;
    margin: 0;
    padding: 0;
  }

  .vs__search {
    color: #2d3748 !important;
    font-size: 0.9rem;
    margin: 0;
    padding: 0;

    &::placeholder {
      color: #a0aec0;
    }
  }

  .vs__dropdown-menu {
    border-radius: 10px;
    border: 1px solid #e0d8ff;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    background: #ffffff;
    z-index: 9999 !important;
    margin-top: 4px;
  }

  .vs__dropdown-option {
    padding: 0.6rem 0.75rem;
    font-size: 0.9rem;
    color: #2d3748;

    &:hover,
    &--highlight {
      background: #f0edff;
      color: #7c5dfa;
    }
  }

  &.vs--disabled {
    .vs__dropdown-toggle {
      background: #f7fafc;
      cursor: not-allowed;
      opacity: 0.7;
    }
  }
}

/* Action Button Filter */
.action-btn-filter {
  height: 42px !important;
  min-height: 42px !important;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.9rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 1.25rem;
  transition: all 0.2s ease;
  border: none;

  svg {
    stroke-width: 2.5;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

/* Pagination Controls Top */
.pagination-controls-top {
  padding: 0.5rem 0;
  border-bottom: 1px solid #f0f0f0;
}

.page-size-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .page-size-label {
    font-size: 0.8rem;
    color: #718096;
    font-weight: 500;
    margin: 0;
  }
}

::v-deep .page-size-select-compact {
  min-width: 70px;

  .vs__dropdown-toggle {
    border-radius: 6px;
    border: 1px solid #e2e8f0;
    min-height: 32px;
    padding: 0.15rem 0.5rem;
  }

  .vs__selected {
    font-size: 0.85rem;
    color: #4a5568;
  }
}

.pagination-count-display {
  .count-text {
    font-size: 0.85rem;
    color: #718096;

    strong {
      color: #4a5568;
    }
  }
}

.pagination-wrapper-compact {
  ::v-deep .pagination {
    margin: 0;

    .page-item {
      .page-link {
        border-radius: 6px;
        margin: 0 2px;
        padding: 0.35rem 0.65rem;
        font-size: 0.8rem;
        font-weight: 500;
        color: #7c5dfa;
        border-color: #e0d8ff;

        &:hover {
          background: #f0edff;
          border-color: #c4b5fd;
        }
      }

      &.active .page-link {
        background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
        border-color: transparent;
        color: #fff;
      }
    }
  }
}

/* Table Scroll Container */
.table-scroll-container {
  position: relative;
}

.table-wrapper {
  overflow-x: auto;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
}

/* Records Table */
::v-deep .records-table {
  margin-bottom: 0;

  thead th {
    background: linear-gradient(180deg, #f8f7ff 0%, #f0edff 100%);
    color: #4a5568;
    font-weight: 600;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 0.85rem 0.75rem;
    border-bottom: 2px solid #e0d8ff;
    white-space: nowrap;
  }

  tbody tr {
    transition: all 0.15s ease;

    &:hover {
      background: #faf9ff;
    }

    td {
      padding: 0.75rem;
      vertical-align: middle;
      border-color: #f0edff;
      font-size: 0.9rem;
      color: #4a5568;
    }
  }
}

/* Action Button Table */
.action-btn-table {
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(124, 93, 250, 0.3);
  }
}

.gap-1 {
  gap: 0.25rem;
}

/* Back Button */
.back-btn {
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0edff;
  border: none;

  &:hover {
    background: #e0d8ff;
  }

  svg {
    color: #7c5dfa;
  }
}

/* Closing Balance Display */
.closing-balance-display {
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border: 1px solid #86efac;
  border-radius: 8px;
  padding: 0.4rem 0.85rem;

  .balance-label {
    font-size: 0.7rem;
    font-weight: 600;
    color: #166534;
    margin-right: 0.5rem;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }

  .balance-value {
    font-size: 1rem;
    font-weight: 700;
    color: #15803d;
  }
}

/* Status Dropdown */
.status-dropdown-wrapper {
  display: flex;
  align-items: center;

  .status-label {
    font-weight: 600;
    color: #4a5568;
    font-size: 0.75rem;
    white-space: nowrap;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }
}

.status-select-native {
  min-width: 100px;
  border-radius: 8px;
  border: 1.5px solid #e0d8ff;
  background: #fff;
  padding: 0.4rem 1.75rem 0.4rem 0.75rem;
  font-weight: 600;
  color: #4a5568;
  font-size: 0.85rem;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%237c5dfa' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.5rem center;
  background-size: 12px;
  transition: all 0.2s ease;

  &:hover {
    border-color: #c4b5fd;
  }

  &:focus {
    border-color: #7c5dfa;
    box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1);
    outline: none;
  }
}

/* Handsontable Wrapper */
.ht-table-wrapper {
  margin-top: 0.75rem;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e0d8ff;
}

/* Handsontable Custom Styling */
::v-deep .ht-theme-main {
  --ht-font-family: inherit;
  --ht-font-size: 11px;
  --ht-cell-horizontal-padding: 6px;
  --ht-cell-vertical-padding: 4px;
  --ht-header-background-color: #f9fafb;
  --ht-header-color: #374151;
  --ht-header-font-weight: 600;
  --ht-cell-background-color: #ffffff;
  --ht-cell-border-color: #e5e7eb;
  --ht-active-border-color: #7c5dfa;
  --ht-selection-background-color: rgba(124, 93, 250, 0.08);
  --ht-selection-border-color: #7c5dfa;

  .handsontable {
    font-family: inherit;

    /* Opening balance row styling */
    .opening-balance-row {
      background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%) !important;
      font-weight: 700 !important;
      color: #166534 !important;
    }

    /* Total balance row styling */
    .total-balance-row {
      background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%) !important;
      font-weight: 700 !important;
      color: #1e40af !important;
    }

    /* Disabled cell styling */
    .disabled-cell {
      background: #f9fafb !important;
      color: #9ca3af !important;
    }

    th {
      background: #f9fafb !important;
      color: #374151 !important;
      font-weight: 600 !important;
      text-transform: uppercase;
      font-size: 0.65rem;
      letter-spacing: 0.3px;
    }

    td {
      color: #374151;
      font-weight: 500;
      font-size: 0.75rem;
    }

    .htNumeric {
      text-align: right;
    }

    .htCheckboxRendererInput {
      cursor: pointer;
    }
  }
}

/* Skeleton Loader */
.skeleton-row {
  .skeleton-cell {
    .skeleton-loader {
      height: 16px;
      background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
      background-size: 200% 100%;
      animation: shimmer 1.5s infinite;
      border-radius: 4px;
    }
  }
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

/* Responsive Design */
@media (max-width: 991px) {
  .pagination-controls-top {
    flex-direction: column;
    gap: 0.75rem;
    align-items: flex-start !important;

    .pagination-count-display {
      order: -1;
    }
  }
}

@media (max-width: 767px) {
  .bank-reconciliation-card,
  .existing-records-card,
  .reconciliation-table-card {
    padding: 1rem;
    border-radius: 12px;
  }

  .filter-row-single-line {
    > [class*="col-"] {
      margin-bottom: 0.75rem;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  .table-header {
    flex-direction: column;
    gap: 0.75rem;
    align-items: flex-start !important;

    .d-flex.align-items-center {
      width: 100%;
      flex-wrap: wrap;
      gap: 0.5rem;
    }
  }

  .closing-balance-display {
    order: -1;
    width: 100%;
    justify-content: center;
    margin-bottom: 0.5rem;
  }

  .action-btn-filter {
    width: 100%;
  }
}
</style>

